/**
 * Lockstep Platform SDK for TypeScript
 *
 * (c) 2021-2023 Lockstep, Inc.
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 *
 * @author     Lockstep Network <support@lockstep.io>
 * @copyright  2021-2023 Lockstep, Inc.
 * @link       https://github.com/Lockstep-Network/lockstep-sdk-typescript
 */
export class ProfilesAccountingContactsClient {
    /**
     * Internal constructor for this client library
     */
    constructor(client) {
        this.client = client;
    }
    /**
     * Retrieves the Accounting Profile Contact specified by this unique identifier, optionally including nested data sets.
     *
     * A Contact has a link to a Contact that is associated with your company's Accounting Profile. A profile has one primary contact and any number of secondary contacts.
     *
     * @param id The unique Lockstep Platform ID number of this Accounting Profile Contact
     */
    retrieveAccountingProfileContact(id) {
        const url = `/api/v1/profiles/accounting/contacts/${id}`;
        return this.client.request("get", url, null, null);
    }
    /**
     * Delete the Accounting Profile Contact referred to by this unique identifier.
     *
     * An Accounting Profile Contact has a link to a Contact that is associated with your company's Accounting Profile. A profile has one primary contact and any number of secondary contacts.
     *
     * @param id The unique Lockstep Platform ID number of the Accounting Profile Contact to delete
     */
    deleteAccountingProfileContact(id) {
        const url = `/api/v1/profiles/accounting/contacts/${id}`;
        return this.client.request("delete", url, null, null);
    }
    /**
     * Creates one or more Accounting Profile Contacts from a given model.
     *
     * An Accounting Profile Contact has a link to a Contact that is associated with your company's Accounting Profile. A profile has one primary contact and any number of secondary contacts.
     *
     * @param body The Accounting Profile Contacts to create
     */
    createAccountingProfileContacts(body) {
        const url = `/api/v1/profiles/accounting/contacts`;
        return this.client.request("post", url, null, body);
    }
    /**
     * Queries Accounting Profile Contacts for this account using the specified filtering, sorting, nested fetch, and pagination rules requested.
     *
     * More information on querying can be found on the [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight) page on the Lockstep Developer website.
     *
     * An Accounting Profile Contact has a link to a Contact that is associated with your company's Accounting Profile. A profile has one primary contact and any number of secondary contacts.
     *
     * @param filter The filter for this query. See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. Available collections: None
     * @param order The sort order for this query. See See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageSize The page size for results (default 250, maximum of 500). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageNumber The page number for results (default 0). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     */
    queryAccountingProfileContacts(filter, include, order, pageSize, pageNumber) {
        const url = `/api/v1/profiles/accounting/contacts/query`;
        const options = {
            params: {
                filter,
                include,
                order,
                pageSize,
                pageNumber,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     * Queries Accounting Profile Contacts and Contacts for this account using the specified filtering, sorting, nested fetch, and pagination rules requested.
     *
     * More information on querying can be found on the [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight) page on the Lockstep Developer website.
     *
     * An Accounting Profile Contact has a link to a Contact that is associated with your company's Accounting Profile. A profile has one primary contact and any number of secondary contacts.
     *
     * A Contact contains information about a person or role within a Company. You can use Contacts to track information about who is responsible for a specific project, who handles invoices, or information about which role at a particular customer or vendor you should speak with about invoices.
     *
     * @param filter The filter for this query. See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param order The sort order for this query. See See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. Available collections: None
     * @param pageSize The page size for results (default 250, maximum of 500). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageNumber The page number for results (default 0). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     */
    queryLinkedAccountingProfileContacts(filter, order, include, pageSize, pageNumber) {
        const url = `/api/v1/profiles/accounting/contacts/query/models`;
        const options = {
            params: {
                filter,
                order,
                include,
                pageSize,
                pageNumber,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     * Reverses the isPrimary fields on the primary and secondary contact to reflect a swap and returns the new primary accounting profile contact model.
     *
     * An Accounting Profile Contact has a link to a Contact that is associated with your company's Accounting Profile. A profile has one primary contact and any number of secondary contacts.
     *
     * @param id The unique Lockstep Platform ID number of the Accounting Profile Contact to set as primary
     */
    setSecondaryContactasPrimary(id) {
        const url = `/api/v1/profiles/accounting/contacts/${id}/primary`;
        return this.client.request("patch", url, null, null);
    }
}
//# sourceMappingURL=ProfilesAccountingContactsClient.js.map